"use client";

/**
 * Site config provider + context.
 *
 * One provider mounted at the root of the app owns the live polling of
 * /site-config.json and exposes the current value to the rest of the tree
 * via React context. Consumers read it via the `useSiteConfig` hook.
 *
 * Two provider variants share the same context so a closer provider wins:
 *
 *   - `LiveSiteConfigProvider` (no props) — wraps the public site at the
 *     root of `app/layout.tsx`. Owns state, runs a 60-second poll, picks
 *     up the inline bootstrap script's value on first paint, persists to
 *     localStorage, and degrades to defaultConfig on network failure.
 *
 *   - `SiteConfigProvider` (takes `config` prop) — used by the admin
 *     preview to inject draft config without polling. The preview tree
 *     sits inside the live provider, so the static prop wins for that
 *     subtree (closest provider semantics) and the live polling
 *     continues underneath.
 *
 * CloudFront serves /site-config.json from the CMS config bucket. A publish
 * invalidates it and writes it with `Cache-Control: max-age=60`, so admin
 * changes appear on the live site within ~1 minute.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  defaultConfig,
  normalizeSiteConfig,
  type SiteConfig,
} from "./siteConfig";

const SiteConfigContext = createContext<SiteConfig | null>(null);

const CONFIG_URL = "/site-config.json";
const STORAGE_KEY = "augustine.site-config";
const POLL_MS = 60_000;

declare global {
  interface Window {
    __AUGUSTINE_SITE_CONFIG__?: Partial<SiteConfig>;
  }
}

function getBootstrapConfig(): SiteConfig | null {
  if (typeof window === "undefined") return null;

  const bootstrap = window.__AUGUSTINE_SITE_CONFIG__;
  if (bootstrap) return normalizeSiteConfig(bootstrap);

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return normalizeSiteConfig(JSON.parse(raw) as Partial<SiteConfig>);
  } catch {
    return null;
  }
}

function persistConfig(config: SiteConfig) {
  if (typeof window === "undefined") return;
  window.__AUGUSTINE_SITE_CONFIG__ = config;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch {
    // Ignore storage write failures.
  }
}

async function fetchConfig(): Promise<SiteConfig | null> {
  try {
    // "no-cache" revalidates every time (a cheap 304 when unchanged). The
    // default mode let browsers answer from heuristic cache: the object had
    // no Cache-Control, so a config last modified months ago was treated as
    // fresh for days and returning visitors never saw a publish.
    const res = await fetch(CONFIG_URL, { cache: "no-cache" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = normalizeSiteConfig((await res.json()) as Partial<SiteConfig>);
    persistConfig(data);
    return data;
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[SiteConfig] fetch failed:", err);
    }
    return null;
  }
}

export function LiveSiteConfigProvider({
  initialConfig,
  children,
}: {
  initialConfig?: SiteConfig;
  children: ReactNode;
}) {
  // Seeded from the build-time config (config/site-config.json) so the
  // prerendered HTML and the first client render are byte-identical.
  //
  // Reading localStorage or the <head> bootstrap global here instead — as
  // this did previously — makes the first client render differ from the
  // server HTML whenever a visitor has a cached config. That mismatch is
  // why the whole tree used to be wrapped in <ClientOnly>, which in turn
  // shipped an empty <main> to anything that does not run JavaScript.
  //
  // The cached and live values are applied in the effect below instead,
  // which runs after hydration and so cannot desync it.
  const [config, setConfig] = useState<SiteConfig>(
    initialConfig ?? defaultConfig,
  );

  useEffect(() => {
    let cancelled = false;
    // The network value is authoritative, including a lower _version from
    // an out-of-band rollback (e.g. restoring an earlier S3 object version).
    const applyLive = (next: SiteConfig | null) => {
      if (next && !cancelled) setConfig(next);
    };

    // A cached copy (localStorage, or the <head> bootstrap's copy of it) is
    // only a head start for a returning visitor. Never let it replace newer
    // content: one saved before the latest deploy would swap the fresh
    // prerendered page back to old content until the network value lands.
    const cached = getBootstrapConfig();
    if (cached) {
      setConfig((current) => (cached._version >= current._version ? cached : current));
    }
    fetchConfig().then(applyLive);

    const interval = setInterval(() => {
      fetchConfig().then(applyLive);
    }, POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  return (
    <SiteConfigContext.Provider value={config}>
      {children}
    </SiteConfigContext.Provider>
  );
}

export function SiteConfigProvider({
  config,
  children,
}: {
  config: SiteConfig;
  children: ReactNode;
}) {
  return (
    <SiteConfigContext.Provider value={config}>
      {children}
    </SiteConfigContext.Provider>
  );
}

export function useSiteConfigContext(): SiteConfig | null {
  return useContext(SiteConfigContext);
}
